import { Spinner } from "@chakra-ui/react";
import { Button, Flex, Image, InlineNotification, Text } from "@familyzone/component-library";
import React, { FC, useEffect, useState } from "react";
import { convertEndTimeToSecsTillExpiry } from "js/utils/timeUtils";
import StandardModal from "js/components/shared/StandardModal";
import iconHelpBlue from "assets/IconHelpBlue.svg?url";
import iconClock from "assets/IconClock.svg?url";
import { colours } from "../../../utils/themeUtils";
import { ClassroomSessionTimeSelector } from "../ClassroomSessionTimeSelector";
import { withRouter } from "../Root/withRouter";
import { Box } from "@material-ui/core";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import dayjs from "dayjs";
import { oneMinuteFromNow, padTimes } from "../ClassroomSessionTimeSelectorHelpers";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useClassroomSessionStore } from "stores/v2/classroomSessionStore";

dayjs.extend(customParseFormat);

interface Props {
	open: boolean;
	onExtendError: boolean;
	durationPlaceholder: string;
	scheduledClass: boolean;
	inClassroom: boolean;
	router: IComponentRouter;
}

const ClassroomEditSession: FC<Props> = (props) => {
	const classroomId = useSelectedClassroomStore((state) => state.id);

	const [timeValue, setTimeValue] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	// This error comes from timer and decides whether save button should be disabled
	const [hasTimeError, setHasTimeError] = useState<string | null>(null);
	const [requestOngoing, setRequestOngoing] = useState<boolean | null>(null);
	const [isMonitoringClass, setIsMonitoringClass] = useState(false);

	const onChangeClassroomConfigStore = (state: { selectedClassroomMonitoring: boolean }) => {
		setIsMonitoringClass(state.selectedClassroomMonitoring);
	};

	useEffect(() => {
		const unsubFromClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomConfigStore(newState);
			}
		);
		return () => {
			unsubFromClassroomSessionStore();
		};
	}, []);

	useEffect(() => {
		if (!props.open) {
			setTimeValue(null);
			setError(null);
			setRequestOngoing(null);
		}
		// Only set error text if the form is already open (ie if we're waiting for an API response)
		if (props.open && props.onExtendError) {
			setError("Cannot extend the class due to time restrictions set by your school");
			setRequestOngoing(false);
		}
	}, [props.open, props.onExtendError]);

	const timeSelectedHandler = (value: number | null, hasTimeError: string | null) => {
		setTimeValue(value);
		setError(null);
		setHasTimeError(hasTimeError);
	};

	const handleExtendSession = () => {
		if (!timeValue) return;
		// Ensure the end time is in the future
		const timeObject = dayjs(padTimes(timeValue), "HHmm", true);
		if (timeObject.isBefore(oneMinuteFromNow())) {
			setError("End time cannot be in the past");
			return;
		}

		if (timeValue && !requestOngoing) {
			setTimeValue(null);
			setError(null);
			setRequestOngoing(true);

			void useClassroomSessionStore.getState().handleStartClassSession({
				classroomId: classroomId as string,
				expiry: convertEndTimeToSecsTillExpiry(timeValue),
				router: props.router,
				monitoringClass: isMonitoringClass,
				inClassroom: props.inClassroom,
			});
		} else if (!props.onExtendError) {
			setError("Please select a duration");
		}
	};

	const handleClose = () => {
		useClassroomSessionStore.getState().handleShowEditClassModal({ show: false });
		useClassroomSessionStore.getState().handleSetClassSessionError({ error: false });
		setTimeValue(null);
		setError(null);
		setRequestOngoing(false);
	};

	const handleOpenScheduledClassesGuide = () => {
		window.open("https://help.classwize.net/hc/en-gb/articles/5578778942364", "_blank");
	};

	const renderActions = () => (
		<Button
			variant="primary"
			onClick={handleExtendSession}
			isLoading={requestOngoing ?? false}
			isDisabled={error !== null || hasTimeError !== null}
		>
			Save
		</Button>
	);

	return (
		<StandardModal
			size="medium"
			title={isMonitoringClass ? "Edit Monitoring Session" : "Edit Class Session"}
			actions={props.scheduledClass || !props.durationPlaceholder ? null : renderActions()}
			open={props.open}
			close={handleClose}
		>
			{props.scheduledClass ? (
				<Box>
					<Box className="StandardModal_content--error">Scheduled sessions can’t be edited.</Box>
					<Box display="flex" className="ClassroomSession_tip" onClick={handleOpenScheduledClassesGuide}>
						<Image className="ClassroomSession_tipIcon" src={iconHelpBlue} alt="Open Classwize Guide" />
						<Text className="ClassroomSession_tipLabel">Find out more about scheduling</Text>
					</Box>
				</Box>
			) : (
				<form onSubmit={(ev) => ev.preventDefault()}>
					{props.durationPlaceholder ? (
						<>
							<Box className="ClassroomSession_label">
								<InlineNotification
									status="info"
									notificationDescription={
										<>
											Please enter time in <b>hh:mm</b> format
										</>
									}
								/>
							</Box>
							<Box className="ClassroomSession_label">
								<Image className="ClassroomSession_labelIcon" src={iconClock} alt=""></Image>
								<Text className="ClassroomSession_labelText">In session until</Text>
								<Box>
									<ClassroomSessionTimeSelector preselected={props.durationPlaceholder} onChange={timeSelectedHandler} />
									{error && (
										<Text position="absolute" color={colours.danger.background.strong} width="209px" p="4px 0 0 4px">
											{error}
										</Text>
									)}
								</Box>
							</Box>
						</>
					) : (
						<Flex justifyContent="center" alignItems="center" height="3em">
							<Spinner size="md" />
						</Flex>
					)}
				</form>
			)}
		</StandardModal>
	);
};

export default withRouter(ClassroomEditSession);
